import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
    IRbacTeam,
    IRbacTenant,
    IKpiQueueThreshold,
    IKpiTeamThreshold,
    ITelXLKpiQueueThreshold,
    ITelXLKpiTeamThreshold,
    IWorkflowInfo,
    IRbacUser,
    IRbacUserTenant,
    IAttribute,
    IRbacBusinessUnit,
    IWrap,
    IRbacRole,
    IRbacChannelProviderConfiguration,
    IRbacConversationSortMode,
    ISearchCriteria,
    ISort,
    AgentType,
    IUserDirectory,
    IUserRead,
    IFilter,
    IRbacConversationSummary,
} from '@dxp/core/models';
import { ChannelProviderType, LOGGER_REQUEST_NAME } from '@dxp/core/types';
import { environment } from '@dxp/core/environment';
import { buildQueryString } from '@dxp/core/helpers';

interface IKpIQueueThresholdResult {
    thresholds: string;
    telXLThresholds: string;
}

@Injectable({
    providedIn: 'root',
})
export class RbacApiService {
    readonly #http = inject(HttpClient);

    getTeams(userId: string): Observable<IRbacTeam[]> {
        return this.#http.get<IRbacTeam[]>(
            `${environment.rbacUrl}/teams/bymember/${userId}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Get Teams)',
                ),
            },
        );
    }

    getKpiThresholds(
        id: string,
        entity: 'teams' | 'channels' | 'queues' | 'businessunits' | 'tenants',
    ): Observable<{
        kpiThreshold: IKpiQueueThreshold | IKpiTeamThreshold;
        kpiTelXLThreshold: ITelXLKpiQueueThreshold | ITelXLKpiTeamThreshold;
    } | null> {
        return this.#http
            .get<IKpIQueueThresholdResult>(
                `${environment.rbacUrl}/kpiThresholds/${entity}/${id}`,
                {
                    context: new HttpContext().set(
                        LOGGER_REQUEST_NAME,
                        'Rbac Service (Get KPI Thresholds)',
                    ),
                },
            )
            .pipe(
                map(data => {
                    if (
                        entity === 'tenants' ||
                        entity === 'businessunits' ||
                        entity === 'queues'
                    ) {
                        return data !== null
                            ? {
                                  kpiThreshold: JSON.parse(
                                      data.thresholds,
                                  ) as IKpiQueueThreshold,
                                  kpiTelXLThreshold: JSON.parse(
                                      data.telXLThresholds,
                                  ) as ITelXLKpiQueueThreshold,
                              }
                            : null;
                    }

                    if (entity === 'teams') {
                        return data !== null
                            ? {
                                  kpiThreshold: JSON.parse(
                                      data.thresholds,
                                  ) as IKpiTeamThreshold,
                                  kpiTelXLThreshold: JSON.parse(
                                      data.telXLThresholds,
                                  ) as ITelXLKpiTeamThreshold,
                              }
                            : null;
                    }

                    return null;
                }),
            );
    }

    getWorkflowDetails(): Observable<IWorkflowInfo[]> {
        const url = `${environment.rbacUrl}/workflows/info`;
        return this.#http.get<IWorkflowInfo[]>(url, {
            context: new HttpContext().set(
                LOGGER_REQUEST_NAME,
                'Rbac Service (Get Workflow Details)',
            ),
        });
    }

    setKpiThresholds(
        id: string,
        entity: 'teams' | 'channels' | 'queues' | 'businessunits' | 'tenants',
        kpiThreshold?: IKpiQueueThreshold | IKpiTeamThreshold,
        kpiTelXLThreshold?: ITelXLKpiQueueThreshold | ITelXLKpiTeamThreshold,
    ) {
        const payload = {
            thresholds: kpiThreshold ? JSON.stringify(kpiThreshold) : null,
            telXLThresholds: kpiTelXLThreshold
                ? JSON.stringify(kpiTelXLThreshold)
                : null,
        };

        return this.#http.post<void>(
            `${environment.rbacUrl}/kpiThresholds/${entity}/${id}`,
            payload,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Set KPI Thresholds)',
                ),
            },
        );
    }

    getUser(userId: string): Observable<IRbacUser> {
        return this.#http.get<IRbacUser>(
            `${environment.rbacUrl}/users/${userId}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Get User)',
                ),
            },
        );
    }

    getConnectedUsers(userId: string): Observable<IRbacUserTenant[]> {
        return this.#http.get<IRbacUserTenant[]>(
            `${environment.rbacUrl}/users/${userId}/connectedUsers`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Get Connected Users)',
                ),
            },
        );
    }

    getAllAgentAttributes(): Observable<string[]> {
        return this.#http.get<string[]>(`${environment.rbacUrl}/attributes`, {
            context: new HttpContext().set(
                LOGGER_REQUEST_NAME,
                'Rbac Service (Get All Agent Attributes)',
            ),
        });
    }

    getAgentAttributes(agentId: string): Observable<IAttribute[]> {
        return this.#http.get<IAttribute[]>(
            `${environment.rbacUrl}/resource/${agentId}/attributes`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Get Agent Attributes)',
                ),
            },
        );
    }

    updateAgentAttributes(agentId: string, attributes: IAttribute[]) {
        return this.#http.post<void>(
            `${environment.rbacUrl}/resource/${agentId}/attributes`,
            attributes,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Post Agent Attributes)',
                ),
            },
        );
    }

    getTenant(tenantId: string): Observable<IRbacTenant> {
        return this.#http.get<IRbacTenant>(
            `${environment.rbacUrl}/tenant/${tenantId}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Get Tenant)',
                ),
            },
        );
    }

    getBusinessUnits(): Observable<IRbacBusinessUnit[]> {
        return this.#http.get<IRbacBusinessUnit[]>(
            `${environment.rbacUrl}/businessUnits`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Get Business Unit)',
                ),
            },
        );
    }

    getWrapDetails(): Observable<IWrap> {
        return this.#http.get<IWrap>(environment.rbacUrl + `/wrapup`, {
            context: new HttpContext().set(
                LOGGER_REQUEST_NAME,
                'Rbac Service (Get wrap)',
            ),
        });
    }

    getPermissions(): Observable<IRbacRole> {
        return this.#http.get<IRbacRole>(`${environment.rbacUrl}/me`, {
            context: new HttpContext().set(
                LOGGER_REQUEST_NAME,
                'Rbac Service (Get Permissions)',
            ),
        });
    }

    getChannelProvidersConfiguration(
        channelProviderType: ChannelProviderType,
    ): Observable<IRbacChannelProviderConfiguration[]> {
        return this.#http.get<IRbacChannelProviderConfiguration[]>(
            `${environment.rbacUrl}/channel-providers/${channelProviderType.toLocaleLowerCase()}/configurations`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Get Channel Provider Configuration)',
                ),
            },
        );
    }

    getConversationSortingMode(): Observable<IRbacConversationSortMode> {
        return this.#http.get<IRbacConversationSortMode>(
            `${environment.rbacUrl}/conversation-sorting-configuration`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Get Conversation Sorting Mode)',
                ),
            },
        );
    }

    getConversationSummary(
        tenantId: string,
    ): Observable<IRbacConversationSummary> {
        return this.#http.get<IRbacConversationSummary>(
            `${environment.rbacUrl}/contentmanager/tenants/${tenantId}/config`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Rbac Service (Get Conversation Summary)',
                ),
            },
        );
    }

    getAgents(
        searchCriteria: ISearchCriteria,
        orderBy?: ISort[],
        agentType?: AgentType,
        filters?: {
            requiresName?: boolean;
            requiresEmail?: boolean;
            requiresPhone?: boolean;
        },
        userId?: string,
    ): Observable<IUserDirectory> {
        return this.#http
            .get<{
                total: number;
                results: IUserRead[];
            }>(
                `${environment.rbacUrl}/users?${buildAgentQueryString(searchCriteria, orderBy, agentType, filters?.requiresName, filters?.requiresEmail, filters?.requiresPhone, userId)}`,
                {
                    context: new HttpContext().set(
                        LOGGER_REQUEST_NAME,
                        'Rbac Service (Get Agents)',
                    ),
                },
            )
            .pipe(
                map(response => {
                    return {
                        total: response.total,
                        results: response.results,
                    };
                }),
            );
    }

    getUsers(
        searchCriteria: ISearchCriteria,
        orderBy?: ISort[],
    ): Observable<IUserDirectory> {
        return this.#http
            .get<{
                total: number;
                results: IUserRead[];
            }>(
                `${environment.rbacUrl}/users?${buildUserOnlyQueryString(searchCriteria, orderBy)}`,
                {
                    context: new HttpContext().set(
                        LOGGER_REQUEST_NAME,
                        'Rbac Service (Get Users)',
                    ),
                },
            )
            .pipe(
                map(response => {
                    return {
                        total: response.total,
                        results: response.results,
                    };
                }),
            );
    }
}

function buildUserOnlyQueryString(
    searchCriteria: ISearchCriteria,
    sorts?: ISort[],
    requiresName?: boolean,
    requiresEmail?: boolean,
    requiresPhone?: boolean,
    userId?: string,
): string {
    const filters: IFilter[] = [
        { not: true, column: 'Scopes.Name', equals: 'bp.access' },
    ];

    if (requiresName) {
        filters.push({ column: 'Name', regex: '.+' });
    }

    if (requiresEmail) {
        filters.push({ column: 'EmailAddress', regex: '.+' });
    }

    if (requiresPhone) {
        filters.push({ column: 'PhoneNumber', regex: '.+' });
    }

    if (userId) {
        filters.push({ not: true, column: 'Id', equals: userId });
    }

    return buildQueryString(
        searchCriteria,
        ['Name', 'EmailAddress', 'PhoneNumber'],
        filters,
        sorts,
    );
}

function buildAgentQueryString(
    searchCriteria: ISearchCriteria,
    sorts?: ISort[],
    agentType?: AgentType,
    requiresName?: boolean,
    requiresEmail?: boolean,
    requiresPhone?: boolean,
    userId?: string,
): string {
    const filters: IFilter[] = [
        { column: 'Scopes.Name', equals: 'bp.access' },
        ...(agentType
            ? [{ column: 'Scopes.Claims.Value', equals: agentType }]
            : []),
    ];

    if (requiresName) {
        filters.push({ column: 'Name', regex: '.+' });
    }

    if (requiresEmail) {
        filters.push({ column: 'EmailAddress', regex: '.+' });
    }

    if (requiresPhone) {
        filters.push({ column: 'PhoneNumber', regex: '.+' });
    }

    if (userId) {
        filters.push({ not: true, column: 'Id', equals: userId });
    }

    return buildQueryString(
        searchCriteria,
        ['Name', 'EmailAddress', 'PhoneNumber'],
        filters,
        sorts,
    );
}
